// Module-level singleton for the preview AudioContext. AudioContext is a
// browser-global resource; wrapping it per-component would leak contexts on
// every remount. Hooks wrap this module for React lifecycle integration.
//
// Responsibilities:
// - Lazy init on first call (or eager init via touchForWarmup())
// - Safari-specific MediaStream tap (context.destination goes silent when a
//   >1GB <video> blob is loaded; the MediaStream is independent)
// - invalidate() closes the context so the next schedule rebuilds it (used
//   after video export + after very large blob loads)

import { debugLog } from './debugLog';

const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const IS_SAFARI = /Safari/.test(UA) && !/Chrom(e|ium)|Edg/.test(UA);

let ctx: AudioContext | null = null;
let voiceGain: GainNode | null = null;
let streamDest: MediaStreamAudioDestinationNode | null = null;
let audioEl: HTMLAudioElement | null = null;
let previewSources: AudioBufferSourceNode[] = [];
/** Unsubscribe handle for the mixer-store subscription that keeps
 *  voiceGain.gain.value in sync with fader/mute changes. Set when voiceGain
 *  is created, cleared on invalidate so the next ensureVoiceGain re-subscribes
 *  against the fresh node. */
let voiceFaderUnsub: (() => void) | null = null;

/** Cached on schedule time — subtracted from getElapsed() so the playhead
 *  reflects audible (not queued) audio. */
export let previewOutputLatency = 0;

export function isSafari(): boolean {
  return IS_SAFARI;
}

export function getAudioContext(): AudioContext {
  if (!ctx) {
    // Plain `new AudioContext()` — `latencyHint` triggers a ~300-500ms
    // hidden-buffering path on Safari.
    ctx = new AudioContext();
    // Safari aggressively suspends contexts mid-session: tab switch,
    // background mode, system sleep, even mid-playback when other tabs
    // grab the audio focus. Without auto-resume, the next scheduled
    // event silently drops or fires late (this caused the inconsistent
    // beeps + take-preview stutter symptoms). The listener wakes the
    // context the moment Safari suspends it; resume() is a no-op when
    // the state is already running.
    const ctxRef = ctx;
    ctxRef.addEventListener('statechange', () => {
      if (ctxRef.state === 'suspended') {
        ctxRef.resume().catch((e) =>
          console.warn('[audio] auto-resume after statechange failed', e),
        );
      }
    });
  }
  return ctx;
}

export function ensureVoiceGain(initialGain: number): GainNode {
  const ac = getAudioContext();
  if (voiceGain && voiceGain.context === ac) {
    return voiceGain;
  }
  voiceGain = ac.createGain();
  voiceGain.gain.value = initialGain;

  // Live-subscribe to the mixer store so fader drags + mute toggles take
  // effect mid-playback. Without this, voiceGain.gain stays at whatever
  // initialGain the first ensureVoiceGain call passed in — fader changes
  // would only apply to the NEXT play, never the current one.
  // Lazy-imported to avoid an audio↔store cycle at module load.
  voiceFaderUnsub?.();
  void import('@/stores/mixer').then(({ useMixerStore, effectiveVoiceVolume }) => {
    if (!voiceGain) return;
    voiceFaderUnsub = useMixerStore.subscribe((s) => {
      if (!voiceGain) return;
      // setTargetAtTime would smooth zipper noise but adds a 10ms ramp;
      // direct assignment is what the timeline preview was always doing,
      // and matches the snappy fader feel from the vanilla build.
      voiceGain.gain.value = effectiveVoiceVolume(s);
    });
    // Apply current value immediately in case the store changed between
    // ensureVoiceGain and the subscribe landing.
    voiceGain.gain.value = effectiveVoiceVolume(useMixerStore.getState());
  });

  // Safari: route ONLY through MediaStreamDestination. Connecting to
  // destination AND MediaStream duplicates output whenever Safari's
  // destination isn't in its silent-under-blob-pressure state.
  const canMediaStream = typeof ac.createMediaStreamDestination === 'function';
  if (IS_SAFARI && canMediaStream) {
    try {
      streamDest = ac.createMediaStreamDestination();
      voiceGain.connect(streamDest);
      if (!audioEl) {
        audioEl = document.createElement('audio');
        audioEl.autoplay = true;
        audioEl.setAttribute('playsinline', '');
      }
      return voiceGain;
    } catch (e) {
      console.warn('[audio] MediaStreamDestination setup failed', e);
    }
  }
  voiceGain.connect(ac.destination);
  return voiceGain;
}

/** Safari: re-bind the MediaStream to the bridging <audio> on every play.
 *  Must be called at the start of every preview — stop() clears srcObject to
 *  prevent the last-frame stutter loop. */
export function startSafariAudioTap(): void {
  if (!streamDest || !audioEl) return;
  try {
    audioEl.srcObject = streamDest.stream;
    audioEl.play().catch((err) =>
      console.warn('[audio] mediastream play failed', err)
    );
  } catch (e) {
    console.warn('[audio] mediastream reattach failed', e);
  }
}

export function isSafariMediaStreamPath(): boolean {
  return IS_SAFARI && streamDest !== null;
}

/** Stop + disconnect every scheduled source. Also clears Safari's stream
 *  srcObject to prevent a stutter loop from the final-frame buffer. */
export function stopAllPreviewSources(): void {
  for (const s of previewSources) {
    try { s.stop(); } catch { /* ignore */ }
    try { s.disconnect(); } catch { /* ignore */ }
  }
  previewSources = [];
  if (audioEl) {
    try { audioEl.pause(); audioEl.srcObject = null; } catch { /* ignore */ }
  }
}

export function trackPreviewSource(s: AudioBufferSourceNode): void {
  previewSources.push(s);
}

/** Callbacks invoked when the preview graph is invalidated. Consumers
 *  register here to drop any context-bound state they cached (e.g. decoded
 *  AudioBuffers on Takes, which become unusable after the context closes
 *  on Safari under memory pressure).
 *
 *  Using a subscription hook (instead of importing the session store here)
 *  keeps the audio module free of UI-tree dependencies. */
type InvalidateListener = (reason: string) => void;
const invalidateListeners = new Set<InvalidateListener>();
export function onPreviewAudioInvalidate(fn: InvalidateListener): () => void {
  invalidateListeners.add(fn);
  return () => invalidateListeners.delete(fn);
}

/** Close + null the context so the next getAudioContext() rebuilds. Use after
 *  heavy ops (video export, loading >200MB blobs). Safari's destination can
 *  silent-degrade when a large blob is attached; a fresh context recovers. */
export function invalidatePreviewAudio(reason: string): void {
  if (!ctx && !voiceGain) return;
  debugLog('[audio] invalidating Web Audio state —', reason);
  try { ctx?.close(); } catch { /* ignore */ }
  ctx = null;
  voiceGain = null;
  streamDest = null;
  // Tear down the mixer subscription — the next ensureVoiceGain rebuilds
  // both the GainNode and the live binding against the fresh context.
  voiceFaderUnsub?.();
  voiceFaderUnsub = null;
  if (audioEl) {
    try { audioEl.pause(); audioEl.srcObject = null; } catch { /* ignore */ }
  }
  // Notify consumers so they can drop decoded AudioBuffer caches that are
  // bound to the now-closed context.
  for (const fn of invalidateListeners) {
    try { fn(reason); } catch { /* ignore */ }
  }
}

/** Best-effort device latency. Value is cached per-schedule so the playhead
 *  formula stays stable during playback.
 *  - getOutputTimestamp: only trust if contextTime < currentTime by ≥5ms
 *    (Safari mirrors the two, which would under-report latency).
 *  - outputLatency / baseLatency: accept if plausibly large.
 *  - Empirical Safari fallback: 0.35s (measured perceptual offset on iOS/macOS). */
export function measureOutputLatency(ac: AudioContext | null): number {
  if (!ac) return 0;
  if (typeof ac.getOutputTimestamp === 'function') {
    try {
      const ts = ac.getOutputTimestamp();
      const ctxTime = ts?.contextTime;
      if (ctxTime !== undefined && isFinite(ctxTime) && ctxTime > 0) {
        const diff = Math.max(0, ac.currentTime - ctxTime);
        if (diff >= 0.005) return diff;
      }
    } catch { /* ignore */ }
  }
  if (ac.outputLatency >= 0.01) return ac.outputLatency;
  if (ac.baseLatency  >= 0.005) return ac.baseLatency;
  return IS_SAFARI ? 0.35 : 0;
}

export function setPreviewOutputLatency(val: number): void {
  previewOutputLatency = val;
}

/** Play a 200ms silent buffer. Forces Safari's DAC pipeline to warm up before
 *  the first real audio — without it, the first Timeline-Play in Safari has a
 *  ~300-500ms perceptual delay. */
export async function warmupSilentBuffer(): Promise<void> {
  const ac = getAudioContext();
  if (ac.state === 'suspended') {
    try { await ac.resume(); } catch { /* ignore */ }
  }
  const buf = ac.createBuffer(1, Math.floor(ac.sampleRate * 0.2), ac.sampleRate);
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.connect(ac.destination);
  src.start();
  // Let the warmup actually run before returning.
  await new Promise<void>((r) => window.setTimeout(r, 200));
}

/** Eager init: call on first user gesture (pointerdown) so the context + DAC
 *  are warm before the first Play click. Safe to call multiple times.
 *
 *  Also kicks off the preroll-beep buffer pre-decode so when the user clicks
 *  Record there's zero decode latency (see prerollBeep.ts). The render uses
 *  an OfflineAudioContext so it runs in parallel with — and doesn't disturb —
 *  the live shared context warmup. */
let eagerInitDone = false;
export async function eagerInit(): Promise<void> {
  if (eagerInitDone) return;
  eagerInitDone = true;
  try {
    await warmupSilentBuffer();
  } catch (e) {
    console.warn('[audio] eager init failed', e);
  }
  // Fire-and-forget — beep buffer caches itself, callers re-await as needed.
  void import('@/lib/prerollBeep').then((m) => m.preloadBeepBuffer()).catch(
    (e) => console.warn('[audio] beep preload failed', e),
  );
}
